/*
 * Recomendador de talla — Mons Boutique
 *
 * Lee la misma tabla de medidas que la Guia de tallas (metaobjeto
 * custom.guia_de_tallas, campo `tabla`), impresa como JSON en un atributo
 * del elemento. Con las medidas de la clienta (cuerpo, en cm) devuelve la
 * talla a pedir en ESTE vestido, en la escala de su propia marca.
 *
 * Reglas de calce, iguales a las del consejo de la Guia de tallas:
 *  - Por cada medida (busto, cintura, cadera) se busca la talla mas chica
 *    cuya fila >= la medida de la clienta. Las filas son medidas del cuerpo
 *    que el vestido calza, y vienen en orden ascendente.
 *  - Se recomienda la MAYOR de las tres: achicar es simple, agrandar casi
 *    nunca en vestidos con corse o pedreria.
 *
 * Nunca normaliza entre marcas: cada producto trae su propia tabla y su
 * propia escala. Un Amoris 2X no es un May Queen 2XL.
 */

class TallaRecomendador extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-tr-form]');
    this.output = this.querySelector('[data-tr-output]');
    if (!this.form || !this.output) return;

    try {
      this.rows = JSON.parse(this.getAttribute('data-tabla') || '[]');
    } catch (e) {
      this.rows = [];
    }
    if (!Array.isArray(this.rows) || this.rows.length === 0) return;

    // Copy editable, inyectado desde el schema del bloque.
    this.copy = {
      single: this.dataset.msgSingle || 'Tu talla recomendada en este vestido:',
      span: this.dataset.msgSpan || 'Tus medidas caen entre la {min} y la {max}. Te recomendamos la {rec}: achicar es simple, agrandar casi nunca.',
      over: this.dataset.msgOver || 'Tus medidas superan la tabla de este vestido. Escribenos y vemos opciones contigo.',
      under: this.dataset.msgUnder || 'Eres la talla mas chica de este vestido ({rec}). Si te queda holgado, lo ajustamos en el atelier.',
      incomplete: this.dataset.msgIncomplete || 'Ingresa al menos tu busto y tu cintura.',
      invalid: this.dataset.msgInvalid || 'Revisa las medidas: usa centimetros (por ejemplo, 88).',
      equiv: this.dataset.msgEquiv || 'equivale a {n}',
    };
    this.waPhone = (this.dataset.waPhone || '').replace(/\D/g, '');
    this.waText = this.dataset.waText || 'Hola, necesito ayuda con la talla de {producto}. {resultado}';
    this.waLabel = this.dataset.waLabel || 'Consultar con la modista por WhatsApp';
    this.producto = this.dataset.producto || '';

    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.recomendar();
    });
  }

  num(name) {
    const el = this.form.elements[name];
    if (!el || el.value.trim() === '') return null;
    const v = parseFloat(el.value.replace(',', '.'));
    return Number.isFinite(v) ? v : NaN;
  }

  // Indice de la talla mas chica cuya fila >= medida. Devuelve
  // { idx, over } donde over=true si la medida supera la fila mayor.
  indiceParaMedida(key, valor) {
    let over = false;
    let idx = this.rows.findIndex((r) => Number(r[key]) >= valor);
    if (idx === -1) {
      idx = this.rows.length - 1;
      over = true;
    }
    return { idx, over };
  }

  etiqueta(row) {
    let t = String(row.talla);
    if (row.equivale_numerica) {
      t += ' (' + this.copy.equiv.replace('{n}', row.equivale_numerica) + ')';
    }
    return t;
  }

  recomendar() {
    const busto = this.num('busto');
    const cintura = this.num('cintura');
    const cadera = this.num('cadera');

    // Validacion: busto y cintura son obligatorios; cadera opcional.
    if (busto === null || cintura === null) {
      return this.render(this.copy.incomplete, null, false);
    }
    const vals = [busto, cintura, cadera].filter((v) => v !== null);
    if (vals.some((v) => Number.isNaN(v)) || vals.some((v) => v < 40 || v > 200)) {
      return this.render(this.copy.invalid, null, false);
    }

    const medidas = [
      ['busto_cm', busto],
      ['cintura_cm', cintura],
      ['cadera_cm', cadera],
    ].filter(([, v]) => v !== null);

    let anyOver = false;
    const indices = medidas.map(([key, valor]) => {
      const { idx, over } = this.indiceParaMedida(key, valor);
      if (over) anyOver = true;
      return idx;
    });

    if (anyOver) {
      return this.render(this.copy.over, 'fuera de tabla', true);
    }

    const maxIdx = Math.max(...indices);
    const minIdx = Math.min(...indices);
    const rec = this.rows[maxIdx];
    const recTxt = this.etiqueta(rec);

    // Talla mas chica y la clienta esta por debajo de esa fila.
    const bustoBajoMinimo = busto < Number(this.rows[0].busto_cm);
    if (maxIdx === 0 && bustoBajoMinimo) {
      return this.render(this.copy.under.replace('{rec}', recTxt), rec.talla, true);
    }

    if (minIdx !== maxIdx) {
      const msg = this.copy.span
        .replace('{min}', this.rows[minIdx].talla)
        .replace('{max}', recTxt)
        .replace('{rec}', recTxt);
      return this.render(msg, rec.talla, true);
    }

    return this.render(this.copy.single, recTxt, true, true);
  }

  // texto: mensaje; talla: valor para el prefill de WhatsApp (o null);
  // conCTA: muestra el boton de WhatsApp; tallaGrande: destaca la talla como dato.
  render(texto, talla, conCTA, tallaGrande) {
    let html = '';
    if (tallaGrande && talla) {
      html += '<p class="tr__result-label">' + this.escape(texto) + '</p>';
      html += '<p class="tr__result-size">' + this.escape(talla) + '</p>';
    } else {
      html += '<p class="tr__result-msg">' + this.escape(texto) + '</p>';
    }

    if (conCTA && this.waPhone) {
      const resultado = talla ? ('Mi talla estimada seria ' + talla + '.') : 'Mis medidas quedaron fuera de la tabla.';
      const msg = this.waText
        .replace('{producto}', this.producto)
        .replace('{resultado}', resultado);
      const href = 'https://wa.me/' + this.waPhone + '?text=' + encodeURIComponent(msg);
      html += '<a class="mons-btn mons-btn--secundario tr__cta" href="' + href + '" target="_blank" rel="noopener">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true" focusable="false">' +
        '<path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884"/>' +
        '</svg>' + this.escape(this.waLabel) + '</a>';
    }

    this.output.innerHTML = html;
    this.output.hidden = false;
    // El resultado se anuncia solo por aria-live; mover el foco seria intrusivo.
  }

  escape(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
  }
}

if (!customElements.get('talla-recomendador')) {
  customElements.define('talla-recomendador', TallaRecomendador);
}
